import {
    QuillFontsEnum
} from '../../../../enums/quill-fonts.enum';
import {
    FONT_FAMILY_WHITE_LIST
} from '../quill.const';

const genericFontFamilies = ['serif', 'sans-serif', 'monospace'];

// @dynamic

/**
 * Contains functions to translate pasted text to useable html.  Translates font-family
 * to values that will work in quill.js
 */
export class FontStyleHelper {

    /**
     * Returns closest matching font whitelisted for quill editor
     */
    public static getMatchingQuillFont(fontFamilyQuery: string): string {
        let closestFontMatch = FontStyleHelper.getMatchingFontForHelveticaAndArial(fontFamilyQuery);

        if (closestFontMatch) {
            return closestFontMatch;
        }

        const fontFamilyPriorityOrder = FontStyleHelper.destructureFontFamilyIntoPrioirtyOrder(fontFamilyQuery);

        for (const fontFamily of fontFamilyPriorityOrder) {
            if (fontFamily in genericFontFamilies) {
                closestFontMatch = FontStyleHelper.getMatchingGenericFont(fontFamily);

                break;
            }

            const potentialWhiteListMatches = FONT_FAMILY_WHITE_LIST.filter(whiteListFont => whiteListFont.indexOf(fontFamily) > -1);
            if (!potentialWhiteListMatches || !potentialWhiteListMatches.length) {
                continue;
            }

            const sortedWhiteListValues = potentialWhiteListMatches.sort((a, b) => {
                const aPriorityIndex = a.toLowerCase().split(',').map(x => x.trim()).findIndex(x => x === fontFamily.toLowerCase());
                const bPriorityIndex = b.toLowerCase().split(',').map(x => x.trim()).findIndex(x => x === fontFamily.toLowerCase());

                if (aPriorityIndex < bPriorityIndex) {
                    return -1;
                }

                if (aPriorityIndex > bPriorityIndex) {
                    return 1;
                }

                return 0;
            });

            closestFontMatch = sortedWhiteListValues[0];

            break;
        }

        return closestFontMatch;
    }

    /**
     * Destructure font family into prority order
     * @param fontFamily: string - "Open Sans, Arial, Helvetica, sans-serif"
     * @returns string[] - ['Arial', 'Helvetica', 'sans-serif']
     */
    public static destructureFontFamilyIntoPrioirtyOrder = (fontFamily: string): string[] => {
        return fontFamily.replace(/['"]/g, '').split(',').map(x => x.trim());
    };

    /**
     * Handles matching fonts to whitelist that contain both Helvetica & Arial families
     */
    public static getMatchingFontForHelveticaAndArial = (fontFamily: string): string => {
        const isHelveticaInFont = fontFamily.match(/[H|h]elvetica/);
        const isArialInFont = fontFamily.match(/[A|a]rial/);

        if (!isHelveticaInFont || !isArialInFont) {
            return;
        }

        if (isHelveticaInFont.index < isArialInFont.index) {
            return QuillFontsEnum.helvetica;
        }

        return QuillFontsEnum.arial;
    };

    public static getMatchingGenericFont = (fontFamily: string): string => {
        let matchingFontFamily = '';

        switch (fontFamily) {
            case 'serif':
                matchingFontFamily = QuillFontsEnum.times;
                break;
            case 'monospace':
                matchingFontFamily = QuillFontsEnum.courierNew;
                break;
            case 'sans-serif':
            default:
                matchingFontFamily = QuillFontsEnum.arial;
                break;
        }

        return matchingFontFamily;
    };
}
